/**
 * Apply Bid Drawer
 * Reusable bottom sheet for applying to jobs - used in JobsPage and JobDetailPage
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, FileText, Users, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import confetti from 'canvas-confetti';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { cn } from '../../utils/cn';
import type { JobCardData } from '../../types/jobs';
import { usePerformanceDetailsStore } from '../../stores/performanceDetailsStore';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import {
  getTafPercentForSubscriptions,
  resolveMembershipPlanKey,
} from '../../data/membershipPlansData';
import { ArtistService } from '../../services/artistService';

function PillWithLabel({
  icon: Icon,
  value,
  label,
  className,
}: {
  icon: React.ElementType;
  value: string;
  label: string;
  className?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const [toggled, setToggled] = useState(false);
  const showLabel = hovered || toggled;
  return (
    <button
      type="button"
      className={cn(
        'group/pill relative inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full border border-[#3A3A3C] bg-[#252527] text-xs font-medium text-white min-w-[5.5rem] overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E]',
        className
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setToggled((t) => !t)}
      aria-label={`${label}: ${value}`}
    >
      <Icon className="h-3.5 w-3.5 text-[#8E8E93] flex-shrink-0" />
      <span className="relative block min-w-0">
        <AnimatePresence mode="wait" initial={false}>
          {!showLabel ? (
            <motion.span
              key="value"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="block truncate"
            >
              {value}
            </motion.span>
          ) : (
            <motion.span
              key="label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="block truncate text-[#8E8E93]"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </button>
  );
}

/** Parse client budget string (e.g. "₹50,000" or "50000") to number */
function parseClientBudget(display: string | undefined): number {
  if (!display || typeof display !== 'string') return 0;
  const cleaned = display.replace(/[₹,\s]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export interface ApplyBidDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: JobCardData | null;
  onConfirm: () => void;
  /** Called after a bid is successfully submitted (so Jobs page can refetch bids/gigs). */
  onBidSuccess?: () => void;
  /** Artist's base price for this event type (from event pricing API). Shown in budget comparison. */
  eventPricingBasePrice?: number | null;
  /** Client budget display string (e.g. "₹50,000"). If not passed, falls back to job.budgetDisplay / job.amount. */
  clientBudgetDisplay?: string;
}

export const ApplyBidDrawer: React.FC<ApplyBidDrawerProps> = ({
  open,
  onOpenChange,
  job,
  onConfirm,
  onBidSuccess,
  eventPricingBasePrice = null,
  clientBudgetDisplay,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [bidAmount, setBidAmount] = useState('');
  const [undertakingChecked, setUndertakingChecked] = useState(false);
  const [personalMessage, setPersonalMessage] = useState('');

  // Step 2 – TBL & tech rider (performance details shown from API)
  const [useSavedTBLAndTechRider, setUseSavedTBLAndTechRider] = useState(true);
  const [tblDetails, setTblDetails] = useState('');
  const [techRiderDetails, setTechRiderDetails] = useState('');
  const [bidSuccess, setBidSuccess] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const latestOnOpenChangeRef = useRef(onOpenChange);

  const navigate = useNavigate();
  const performanceDetailsData = usePerformanceDetailsStore((s) => s.data);
  const ensurePerformanceDetailsFetched = usePerformanceDetailsStore(
    (s) => s.ensureFetched
  );
  const subscriptions = useArtistDetailsStore((s) => s.data?.subscriptions);

  const numericBidAmount = useMemo(() => {
    const value = Number(bidAmount);
    return Number.isFinite(value) ? value : 0;
  }, [bidAmount]);

  const clientBudgetNumber = useMemo(() => {
    const raw = clientBudgetDisplay ?? job?.budgetDisplay ?? (job ? `₹${job.amount.toLocaleString()}` : undefined);
    return parseClientBudget(raw);
  }, [clientBudgetDisplay, job?.budgetDisplay, job?.amount]);
  const isExpressJob = useMemo(
    () => (job?.type ?? '').trim().toLowerCase() === 'express',
    [job?.type]
  );
  const effectiveBidAmount = useMemo(
    () => (isExpressJob ? clientBudgetNumber : numericBidAmount),
    [isExpressJob, clientBudgetNumber, numericBidAmount]
  );
  const isBidAmountBelowMinimum = useMemo(
    () => !isExpressJob && bidAmount.trim().length > 0 && numericBidAmount < 3000,
    [isExpressJob, bidAmount, numericBidAmount]
  );

  const planKey = useMemo(
    () => resolveMembershipPlanKey(subscriptions),
    [subscriptions?.subscription_name, subscriptions?.subscription_plan]
  );
  const isFreeOrEconomyPlan = useMemo(
    () => planKey === 'free' || planKey === 'economy',
    [planKey]
  );
  const tafPercent = useMemo(
    () => getTafPercentForSubscriptions(subscriptions),
    [subscriptions?.subscription_name, subscriptions?.subscription_plan]
  );
  const showUpgradePlanCta = planKey !== 'elite';

  useEffect(() => {
    latestOnOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    if (!open || !job) return;
    if (isExpressJob) {
      setBidAmount(clientBudgetNumber > 0 ? String(clientBudgetNumber) : '');
      return;
    }
    setBidAmount('');
  }, [open, job, isExpressJob, clientBudgetNumber]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setStep(1);
      setBidAmount('');
      setUndertakingChecked(false);
      setPersonalMessage('');
      setUseSavedTBLAndTechRider(true);
      setTblDetails('');
      setTechRiderDetails('');
      setBidSuccess(false);
      setSubmitLoading(false);
    }
    onOpenChange(next);
  };

  // When step 2 is shown, ensure performance details are loaded
  useEffect(() => {
    if (open && step === 2) {
      ensurePerformanceDetailsFetched();
    }
  }, [open, step, ensurePerformanceDetailsFetched]);

  // When user unchecks "use saved TBL and tech rider", fetch and populate from API
  useEffect(() => {
    if (!open || useSavedTBLAndTechRider) return;
    let cancelled = false;
    ArtistService.getTechRiderTbl()
      .then((data) => {
        if (cancelled) return;
        setTblDetails(data.tbl ? data.tbl_desc ?? '' : '');
        setTechRiderDetails(data.tech_rider ? data.tech_rider_desc ?? '' : '');
      })
      .catch(() => {
        if (!cancelled) {
          setTblDetails('');
          setTechRiderDetails('');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, useSavedTBLAndTechRider]);

  const handlePrimaryNextFromStep1 = () => {
    // Enforce minimum pitching amount
    if (!isExpressJob && numericBidAmount < 3000) {
      toast.error('Minimum amount for pitching is ₹3000');
      return;
    }

    if (!undertakingChecked) {
      toast.error('Please accept the undertaking to continue.');
      return;
    }

    setStep(2);
  };

  const handlePrimaryNextFromStep2 = () => {
    setStep(3);
  };

  const handleConfirm = async () => {
    if (!job) return;
    if (!isExpressJob && numericBidAmount < 3000) {
      toast.error('Minimum amount for pitching is ₹3000');
      setStep(1);
      return;
    }
    if (!undertakingChecked) {
      toast.error('Please accept the undertaking to continue.');
      setStep(1);
      return;
    }

    setSubmitLoading(true);
    const payload: {
      bid: number;
      message: string;
      job_tbl?: string;
      job_tech_rider?: string;
    } = {
      bid: effectiveBidAmount,
      message: personalMessage.trim(),
    };
    if (!useSavedTBLAndTechRider) {
      payload.job_tbl = tblDetails;
      payload.job_tech_rider = techRiderDetails;
    }

    try {
      await ArtistService.submitJobBid(job.id, payload);
      setBidSuccess(true);
      onBidSuccess?.();
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
      confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
      confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
    } catch {
      toast.error('Failed to submit bid. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleUndertakingChange = (checked: boolean) => {
    setUndertakingChecked(checked);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {job && (
        <div className="space-y-5 pt-2 w-full">
          <div className="flex items-center justify-between text-xs text-[#8E8E93]">
            <span className="uppercase tracking-widest font-semibold">Pitch Job</span>
            {!bidSuccess && (
              <span>
                Step {step} of 3
              </span>
            )}
          </div>

          {bidSuccess ? (
            <div className="py-8 flex flex-col items-center justify-center text-center space-y-4">
              <CheckCircle2 className="h-16 w-16 text-emerald-500" />
              <h2 className="text-xl font-bold text-white">Job successfully applied!</h2>
              <p className="text-sm text-[#8E8E93]">Your bid has been forwarded to the client.</p>
              {isFreeOrEconomyPlan && (
                <div className="w-full max-w-md rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] p-4 space-y-3">
                  <p className="text-sm text-[#C7C7CC]">
                    Note: Professional names of Free / Economy members are masked in the submitted bids.
                    It is only visible for Premium / Elite members only.
                  </p>
                  <Button
                    variant="active"
                    type="button"
                    onClick={() => navigate('/membership')}
                    className="w-full"
                  >
                    Upgrade Now
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <span className="inline-block px-3 py-1.5 rounded-full bg-[#F33363]/20 border border-[#F33363]/40 text-[#F33363] text-sm font-medium">
                Client's budget: {job.budgetDisplay ?? `₹${job.amount.toLocaleString()}`}
              </span>
              {step === 1 && (
                <>
                  <h2 className="text-xl font-bold text-white">
                    {isExpressJob ? 'Review & Accept This Booking' : 'Bid your fee for this Event'}
                  </h2>

                  <div>
                    {isExpressJob ? (
                      <div className="rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] px-4 py-4">
                        <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-widest">
                          Your bid amount
                        </p>
                        <p className="mt-1 text-xl font-semibold text-emerald-400">
                          {job.budgetDisplay ?? `₹${job.amount.toLocaleString()}`}
                        </p>
                      </div>
                    ) : (
                      <>
                        <label className="sr-only">Your bid amount</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/4 text-[#8E8E93] text-2xl">₹</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={bidAmount}
                            onChange={(e) => setBidAmount(e.target.value.replace(/\D/g, ''))}
                            placeholder="0"
                            className={cn(
                              'w-full pl-10 pr-4 py-4 rounded-xl border border-[#3A3A3C] bg-transparent text-xl font-semibold transition-colors',
                              bidAmount ? 'text-emerald-400 border-[#3A3A3C]' : 'text-white placeholder-[#8E8E93] border-[#3A3A3C]'
                            )}
                          />
                        </div>
                        {isBidAmountBelowMinimum && (
                          <p className="mt-2 text-xs text-red-400">
                            Minimum allowed bid amount is ₹3000.
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Budget comparison: event rate, client budget vs bid */}
                  {(eventPricingBasePrice != null || clientBudgetNumber > 0 || effectiveBidAmount > 0) && (
                    <div className="rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] px-3 py-2.5 space-y-1.5 text-xs">
                      {eventPricingBasePrice != null && eventPricingBasePrice > 0 && (
                        <p className="text-[#8E8E93]">
                          Your rate for this event type: <span className="font-semibold text-white">₹{eventPricingBasePrice.toLocaleString()}</span>
                        </p>
                      )}
                      {clientBudgetNumber > 0 && effectiveBidAmount > 0 && (
                        <>
                          {effectiveBidAmount > clientBudgetNumber && (
                            <p className="text-amber-400">
                              Your bid exceeds client budget (₹{clientBudgetNumber.toLocaleString()}).
                            </p>
                          )}
                          {effectiveBidAmount <= clientBudgetNumber && effectiveBidAmount > 0 && (
                            <p className="text-emerald-400">
                              Your bid is within client budget.
                            </p>
                          )}
                        </>
                      )}
                      {clientBudgetNumber > 0 && eventPricingBasePrice != null && eventPricingBasePrice > 0 && clientBudgetNumber < eventPricingBasePrice && (
                        <p className="text-amber-400">
                          Client budget is below your set rate for this event (₹{eventPricingBasePrice.toLocaleString()}).
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <PillWithLabel icon={Clock} value={`${job.durationMinutes} min`} label="Duration" />
                    <PillWithLabel icon={Users} value={String(job.attendees)} label="Audience" />
                    <PillWithLabel icon={Calendar} value={job.date} label="Date" />
                    <PillWithLabel icon={FileText} value={job.eventTag} label="Event Type" />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleUndertakingChange(!undertakingChecked)}
                    className="flex items-start gap-3 cursor-pointer focus:outline-none"
                    role="checkbox"
                    aria-checked={undertakingChecked}
                  >
                    <span
                      className={cn(
                        'mt-1 h-4 w-4 rounded border border-[#3A3A3C] bg-[#2C2C2E] flex items-center justify-center transition-colors',
                        undertakingChecked && 'border-[#F33363] bg-[#F33363]'
                      )}
                    >
                      {undertakingChecked && (
                        <span className="block h-2 w-2 rounded-sm bg-white" />
                      )}
                    </span>
                    <span className="text-sm text-[#8E8E93] text-left">
                      I undertake to perform at this event in the above quoted fee above
                    </span>
                  </button>

                  <div>
                    <label className="sr-only">Personal message to client</label>
                    <textarea
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      placeholder="Add a personal message (sent to the client)"
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-white placeholder-[#8E8E93] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent"
                    />
                  </div>

                  <Button
                    variant="active"
                    onClick={handlePrimaryNextFromStep1}
                    className="w-full py-3.5 font-medium"
                  >
                    Next
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="text-xl font-bold text-white">Performance Details</h2>

                  <div className="space-y-3 rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] p-4">
                    <div>
                      <p className="text-xs font-semibold text-[#8E8E93] mb-0.5">
                        Performing Language
                      </p>
                      <p className="text-sm text-white">
                        {performanceDetailsData?.performing_language?.map((l) => l.language_name).filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#8E8E93] mb-0.5">
                        Performance Duration
                      </p>
                      <p className="text-sm text-white">
                        {(() => {
                          const min = performanceDetailsData?.min_duration_mins;
                          const max = performanceDetailsData?.max_duration_mins;
                          if (min != null && max != null) return `${min}–${max} mins`;
                          if (min != null) return `${min} mins`;
                          return '—';
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#8E8E93] mb-0.5">
                        Offstage Crew
                      </p>
                      <p className="text-sm text-white">
                        {performanceDetailsData?.offstage_crew != null
                          ? String(performanceDetailsData.offstage_crew)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#8E8E93] mb-0.5">
                        Performing Members
                      </p>
                      <p className="text-sm text-white">
                        {performanceDetailsData?.performing_members?.map((m) => m.name).filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-[#8E8E93]">
                    You can modify your details from the{' '}
                    <Link
                      to="/profile/performance-details"
                      className="text-[#F33363] font-medium hover:underline focus:outline-none focus:underline"
                      onClick={() => handleOpenChange(false)}
                    >
                      Performance Details
                    </Link>{' '}
                    section.
                  </p>

                  <div className="mt-2 flex items-start gap-3">
                    <Checkbox
                      checked={useSavedTBLAndTechRider}
                      onCheckedChange={(checked) => setUseSavedTBLAndTechRider(checked)}
                      className="mt-0.5"
                    />
                    <span
                      className="text-sm text-[#8E8E93] text-left cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => setUseSavedTBLAndTechRider((prev) => !prev)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setUseSavedTBLAndTechRider((prev) => !prev);
                        }
                      }}
                    >
                      Use my saved TBL and Tech Rider Details for this job.
                    </span>
                  </div>

                  {!useSavedTBLAndTechRider && (
                    <div className="space-y-3 pt-2">
                      <div>
                        <label className="block text-xs font-semibold text-[#8E8E93] mb-1">
                          TBL Details
                        </label>
                        <textarea
                          value={tblDetails}
                          onChange={(e) => setTblDetails(e.target.value)}
                          placeholder="Share your table / hospitality requirements..."
                          rows={3}
                          className="w-full px-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-sm text-white placeholder-[#8E8E93] resize-none focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-[#8E8E93] mb-1">
                          Tech Rider Details
                        </label>
                        <textarea
                          value={techRiderDetails}
                          onChange={(e) => setTechRiderDetails(e.target.value)}
                          placeholder="Share your sound, stage and technical requirements..."
                          rows={3}
                          className="w-full px-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-sm text-white placeholder-[#8E8E93] resize-none focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3.5 border border-[#3A3A3C] bg-transparent text-white hover:border-[#F33363]/50"
                    >
                      Back
                    </Button>
                    <Button
                      variant="active"
                      type="button"
                      onClick={handlePrimaryNextFromStep2}
                      className="flex-1 py-3.5"
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <h2 className="text-xl font-bold text-white">Review & Confirm</h2>

                  <div className="space-y-4 rounded-2xl bg-[#1C1C1E] border border-[#3A3A3C] p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-widest">
                          Your Bid
                        </p>
                        <p className="text-lg font-bold text-white mt-1">
                          ₹{effectiveBidAmount.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-widest">
                          Client Budget
                        </p>
                        <p className="text-sm font-bold text-white mt-1">
                          {job.budgetDisplay ?? `₹${job.amount.toLocaleString()}`}
                        </p>
                      </div>
                    </div>

                    <div className="h-px w-full bg-[#3A3A3C]/60 my-2" />

                    <div className="grid grid-cols-2 gap-3 text-xs text-[#8E8E93]">
                      <div>
                        <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                          Event
                        </p>
                        <p className="text-white text-sm font-medium line-clamp-2">
                          {job.title ?? `${job.eventType} • ${job.location}`}
                        </p>
                        <p className="mt-1">{job.date}</p>
                      </div>
                      <div>
                        <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                          Logistics
                        </p>
                        <p className="text-white text-sm">
                          {job.durationMinutes} min • {job.attendees} guests
                        </p>
                        <p className="mt-1">{job.eventTag}</p>
                      </div>
                    </div>

                    <div className="h-px w-full bg-[#3A3A3C]/60 my-2" />

                    <div className="grid grid-cols-2 gap-3 text-xs text-[#8E8E93]">
                      <div>
                        <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                          Performance
                        </p>
                        {(performanceDetailsData?.performing_language?.length ?? 0) > 0 && (
                          <p className="text-white text-sm">
                            Language: {performanceDetailsData?.performing_language?.map((l) => l.language_name).filter(Boolean).join(', ') || '—'}
                          </p>
                        )}
                        {(performanceDetailsData?.min_duration_mins != null || performanceDetailsData?.max_duration_mins != null) && (
                          <p className="text-white text-sm">
                            Duration:{' '}
                            {performanceDetailsData?.min_duration_mins != null && performanceDetailsData?.max_duration_mins != null
                              ? `${performanceDetailsData.min_duration_mins}–${performanceDetailsData.max_duration_mins} mins`
                              : performanceDetailsData?.min_duration_mins != null
                                ? `${performanceDetailsData.min_duration_mins} mins`
                                : `${performanceDetailsData?.max_duration_mins} mins`}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                          Team
                        </p>
                        {performanceDetailsData?.offstage_crew != null && (
                          <p className="text-white text-sm">Offstage: {performanceDetailsData.offstage_crew}</p>
                        )}
                        {(performanceDetailsData?.performing_members?.length ?? 0) > 0 && (
                          <p className="text-white text-sm">
                            Performing: {performanceDetailsData?.performing_members?.map((m) => m.name).filter(Boolean).join(', ') || '—'}
                          </p>
                        )}
                      </div>
                    </div>

                    {!useSavedTBLAndTechRider && (tblDetails || techRiderDetails) && (
                      <>
                        <div className="h-px w-full bg-[#3A3A3C]/60 my-2" />
                        <div className="space-y-2 text-xs text-[#8E8E93]">
                          {tblDetails && (
                            <div>
                              <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                                TBL
                              </p>
                              <p className="text-white text-sm whitespace-pre-line">
                                {tblDetails}
                              </p>
                            </div>
                          )}
                          {techRiderDetails && (
                            <div>
                              <p className="font-semibold mb-1 text-[11px] uppercase tracking-widest">
                                Tech Rider
                              </p>
                              <p className="text-white text-sm whitespace-pre-line">
                                {techRiderDetails}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {personalMessage && (
                      <>
                        <div className="h-px w-full bg-[#3A3A3C]/60 my-2" />
                        <div>
                          <p className="text-[11px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-1">
                            Personal Message
                          </p>
                          <p className="text-white text-sm whitespace-pre-line">
                            {personalMessage}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-3 rounded-2xl border border-[#3A3A3C] bg-[#121214] px-4 py-3 text-xs text-[#C7C7CC] space-y-1">
                    <p className="text-[11px] font-semibold text-white">
                      Kindly Note :
                    </p>
                    <p>1. The following pitch is not eligible for cancellation.</p>
                    <p>2. Your profile will be sent to the client along with your pitch.</p>
                  </div>

                  <div className="flex items-center gap-3 pt-3">
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      disabled={submitLoading}
                      className="flex-1 py-3.5 border border-[#3A3A3C] bg-transparent text-white hover:border-[#F33363]/50"
                    >
                      Back
                    </Button>
                    <Button
                      variant="active"
                      type="button"
                      onClick={handleConfirm}
                      disabled={submitLoading}
                      className="flex-1 py-3.5"
                    >
                      {submitLoading ? 'Submitting...' : 'Confirm'}
                    </Button>
                  </div>
                </>
              )}
              <hr className=" border-gray-700/60" />
              <div>
                {!isExpressJob && (
                  <p className="text-xs text-[#8E8E93] text-center">
                    You are applying with {job.coinsToApply} StarCoins.
                  </p>
                )}

                <p className="text-xs text-[#F33363] text-center mt-1 mb-1">
                  {tafPercent}% TAF (Technology Aggregation Fee) applies to your booking amount.
                </p>
                <hr className=" border-gray-700/60" />
                {showUpgradePlanCta && (
                  <button
                    type="button"
                    onClick={() => navigate('/membership')}
                    className="w-full text-center text-xs text-[#8E8E93] hover:underline focus:outline-none focus:underline mt-2"
                  >
                    Upgrade plan to reduce TAF & get faster payouts
                  </button>
                )}
              </div>

            </>
          )}
        </div>
      )}
    </Sheet>
  );
};
